import {
  ProviderAbsence,
  ProviderAbsenceCreatePayload,
  ProviderAbsenceGetParams,
  ProviderAbsenceUpdatePayload,
} from '../../types/absences/types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { get, post, put, remove } from '../apiService';

const BASE_URL = 'resourceMatrix/provider_absence';

interface ProviderAbsencesResponse {
  status: string;
  absences: Array<{
    absences: ProviderAbsence[];
  }>;
  message?: string;
}

const PROVIDER_ABSENCE_LIST_TTL_MS = 5000;
const providerAbsenceListCache = new Map<
  string,
  {
    timestamp: number;
    result: { absences: ProviderAbsence[]; loading: boolean };
  }
>();
const providerAbsenceListInFlight = new Map<
  string,
  Promise<{ absences: ProviderAbsence[]; loading: boolean }>
>();

export const invalidateProviderAbsenceListCache = (): void => {
  providerAbsenceListCache.clear();
  providerAbsenceListInFlight.clear();
};

/**
 * Normalizes an absence object to ensure consistent field types and values.
 * Handles null/undefined/empty string conversions and ensures practiceIds is always an array.
 */
const normalizeAbsence = (
  absence: ProviderAbsence,
): ProviderAbsence => {
  // Ensure practiceIds is always an array (handle both formats)
  const practiceIds =
    absence.practiceIds ||
    (absence.practiceId ? [absence.practiceId] : []);

  // Normalize endDate: null, undefined, empty string, or whitespace should be null
  const normalizedEndDate =
    absence.endDate &&
    typeof absence.endDate === 'string' &&
    absence.endDate.trim()
      ? absence.endDate.trim()
      : null;

  // Normalize displayDate: null, undefined, empty string, or whitespace should be null
  const normalizedDisplayDate =
    absence.displayDate &&
    typeof absence.displayDate === 'string' &&
    absence.displayDate.trim()
      ? absence.displayDate.trim()
      : null;

  // Normalize startTime: null, undefined, empty string, or whitespace should be undefined
  const normalizedStartTime =
    absence.startTime &&
    typeof absence.startTime === 'string' &&
    absence.startTime.trim()
      ? absence.startTime.trim()
      : undefined;

  // Normalize endTime: null, undefined, empty string, or whitespace should be undefined
  const normalizedEndTime =
    absence.endTime &&
    typeof absence.endTime === 'string' &&
    absence.endTime.trim()
      ? absence.endTime.trim()
      : undefined;

  // Normalize note: prefer note over notes, normalize null/undefined/empty to null
  const normalizedNote =
    absence.note &&
    typeof absence.note === 'string' &&
    absence.note.trim()
      ? absence.note.trim()
      : absence.notes &&
          typeof absence.notes === 'string' &&
          absence.notes.trim()
        ? absence.notes.trim()
        : null;

  return {
    ...absence,
    practiceIds:
      practiceIds.length > 0
        ? practiceIds
        : absence.practiceIds || [],
    endDate: normalizedEndDate,
    displayDate: normalizedDisplayDate,
    startTime: normalizedStartTime,
    endTime: normalizedEndTime,
    note: normalizedNote,
  };
};

const buildQueryString = (
  params: Record<string, string | string[] | undefined>,
): string => {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        // Repeat the key for each value in the array
        return value.map((v) => `${key}=${v}`);
      }
      return `${key}=${value}`;
    })
    .join('&');
};

/**
 * Groups absences by matching attributes and merges practiceIds.
 *
 * This function handles cases where the API returns separate absence entries
 * for the same logical absence across multiple practices. For example, if a
 * provider has the same absence (same dates, times, notes) for Practice A and
 * Practice B, the API returns two entries with different IDs. This function
 * groups them into a single entry with both practiceIds.
 *
 * Grouping Criteria:
 * - Absences are grouped if ALL of the following match:
 *   - providerId
 *   - startDate
 *   - endDate (null/empty are treated as equivalent)
 *   - startTime (null/undefined/empty are treated as equivalent)
 *   - endTime (null/undefined/empty are treated as equivalent)
 *   - displayDate (null/empty are treated as equivalent)
 *   - note/notes (whitespace-normalized and trimmed, null/empty are equivalent)
 *
 * Important: Absences with the same dates but different notes will NOT be
 * grouped together, as they represent different logical absences.
 *
 * @param absences - Array of absences after flattening the nested API response
 * @returns Map where key is a composite string of matching attributes, value is array of matching absences
 */
const groupAbsencesByAttributes = (
  absences: ProviderAbsence[],
): Map<string, ProviderAbsence[]> => {
  const absenceGroups = new Map<string, ProviderAbsence[]>();

  for (const absence of absences) {
    // Create a key based on absence attributes (excluding ID and practiceIds)
    // This groups absences that represent the same logical absence across multiple practices
    // Normalize null/undefined/empty to empty string for consistent grouping
    const normalizedEndDate = absence.endDate || '';
    const normalizedStartTime = absence.startTime || '';
    const normalizedEndTime = absence.endTime || '';
    const normalizedDisplayDate = absence.displayDate || '';
    // Normalize note: trim whitespace, treat null/undefined/empty as empty string
    const normalizedNote = (
      absence.note ||
      absence.notes ||
      ''
    ).trim();

    const groupKey = [
      absence.providerId,
      absence.startDate,
      normalizedEndDate,
      normalizedStartTime,
      normalizedEndTime,
      normalizedDisplayDate,
      normalizedNote,
    ].join('|');

    if (!absenceGroups.has(groupKey)) {
      absenceGroups.set(groupKey, []);
    }
    absenceGroups.get(groupKey)?.push(absence);
  }

  return absenceGroups;
};

/**
 * Merges a group of absences into a single absence entry.
 *
 * All absences in a group share the same attributes (dates, times, notes, provider)
 * but have different practiceIds. This function:
 * 1. Uses the first absence as the base (they're all identical except for practiceIds)
 * 2. Collects all unique practiceIds from all absences in the group
 * 3. Stores all original absence IDs in metadata.relatedAbsenceIds for deletion
 * 4. Uses normalized values from the base absence (already normalized by normalizeAbsence)
 *
 * @param group - Array of absences that have been grouped together
 * @returns Single merged absence with combined practiceIds and metadata
 */
const mergeAbsenceGroup = (
  group: ProviderAbsence[],
): ProviderAbsence => {
  // Use the first absence as the base (they should all have the same attributes except practiceIds)
  // The absence is already normalized by normalizeAbsence function
  const baseAbsence = group[0];

  // Collect all unique practiceIds from all absences in the group
  const allPracticeIds = Array.from(
    new Set(
      group.flatMap((absence) => {
        const ids =
          absence.practiceIds ||
          (absence.practiceId ? [absence.practiceId] : []);
        return ids.filter((id): id is string => Boolean(id?.trim()));
      }),
    ),
  ).filter((id): id is string => Boolean(id?.trim()));

  // Collect all original IDs from the group (for deletion purposes)
  // This includes ALL absence IDs that were merged into this single entry
  // The primary ID (baseAbsence.id) is included in this array
  const allOriginalIds = group
    .map((absence) => absence.id)
    .filter((id): id is string => Boolean(id?.trim()));

  // Since all absences in a group should have the same attributes (based on groupKey),
  // we can safely use the baseAbsence values (already normalized)
  return {
    ...baseAbsence,
    id: baseAbsence.id, // Keep the first ID as primary (for display/UI purposes)
    practiceIds:
      allPracticeIds.length > 0
        ? allPracticeIds
        : baseAbsence.practiceIds || [],
    // Fields are already normalized by normalizeAbsence function
    metadata: {
      ...baseAbsence.metadata,
      // Store all original IDs so we can delete all related absences
      // Only set relatedAbsenceIds if multiple absences were merged (length > 1)
      // For single absences, we'll use the primary ID directly in delete handler
      // The primary ID is always included in this array when it exists
      relatedAbsenceIds:
        allOriginalIds.length > 1 ? allOriginalIds : undefined,
    },
  };
};

export const getProviderAbsences = async (
  params: ProviderAbsenceGetParams,
): Promise<{ absences: ProviderAbsence[]; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer ID is not available');
  }

  const queryParams = buildQueryString({
    customerId: customer,
    providerIds: params.providerIds,
    practiceIds: params.practiceIds,
    marketId: params.marketId, // Add marketId support
    startDate: params.startDate,
    endDate: params.endDate,
  });

  const url = `${BASE_URL}/list?${queryParams}`;
  const cacheKey = url;
  const now = Date.now();
  const cached = providerAbsenceListCache.get(cacheKey);
  if (
    cached &&
    now - cached.timestamp < PROVIDER_ABSENCE_LIST_TTL_MS
  ) {
    return cached.result;
  }

  const inFlight = providerAbsenceListInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  let loading = true;

  const request = (async () => {
    try {
      const responseData = (await get(
        url,
      )) as ProviderAbsencesResponse;
      loading = false;

      if (
        responseData.status === 'success' &&
        Array.isArray(responseData.absences)
      ) {
        // Flatten the nested absences structure: absences[].absences[] -> absences[]
        // The API groups absences by startDate, and each group contains an array of absences
        // that may have different attributes (endDate, displayDate, times, notes, practiceIds)
        const flattenedAbsences: ProviderAbsence[] =
          responseData.absences.flatMap((group) => {
            // Ensure group.absences is an array and normalize each absence
            if (Array.isArray(group.absences)) {
              return group.absences.map((absence) =>
                normalizeAbsence(absence),
              );
            }
            return [];
          });

        // Group absences by matching attributes (excluding practiceIds) and merge practiceIds
        // This handles cases where the same logical absence exists across multiple practices
        const absenceGroups =
          groupAbsencesByAttributes(flattenedAbsences);

        // Merge each group into a single absence with all practiceIds combined
        const mergedAbsences: ProviderAbsence[] = Array.from(
          absenceGroups.values(),
        ).map((group) => {
          return mergeAbsenceGroup(group);
        });

        const result = { absences: mergedAbsences, loading };
        providerAbsenceListCache.set(cacheKey, {
          timestamp: Date.now(),
          result,
        });
        return result;
      } else {
        throw new Error(
          responseData.message || 'Failed to fetch provider absences',
        );
      }
    } catch (error) {
      loading = false;
      console.error('Failed to fetch provider absences:', error);
      throw new Error('Failed to fetch provider absences');
    }
  })();

  providerAbsenceListInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    providerAbsenceListInFlight.delete(cacheKey);
  }
};

// New function to batch fetch absences for multiple providers
export const getBatchProviderAbsences = async (
  providerIds: string[],
  practiceIds?: string[],
  startDate?: string,
  endDate?: string,
): Promise<{ absences: ProviderAbsence[]; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer ID is not available');
  }

  const queryParams = buildQueryString({
    customerId: customer,
    providerIds: providerIds,
    practiceIds: practiceIds,
    startDate: startDate,
    endDate: endDate,
  });

  const url = `${BASE_URL}/list?${queryParams}`;
  let loading = true;

  try {
    const responseData = (await get(url)) as ProviderAbsencesResponse;
    loading = false;

    if (
      responseData.status === 'success' &&
      Array.isArray(responseData.absences)
    ) {
      // Flatten the nested absences structure: absences[].absences[] -> absences[]
      // The API groups absences by startDate, and each group contains an array of absences
      // that may have different attributes (endDate, displayDate, times, notes, practiceIds)
      const flattenedAbsences: ProviderAbsence[] =
        responseData.absences.flatMap((group) => {
          // Ensure group.absences is an array and normalize each absence
          if (Array.isArray(group.absences)) {
            return group.absences.map((absence) =>
              normalizeAbsence(absence),
            );
          }
          return [];
        });

      // Group absences by matching attributes (excluding practiceIds) and merge practiceIds
      // This handles cases where the same logical absence exists across multiple practices
      const absenceGroups =
        groupAbsencesByAttributes(flattenedAbsences);

      // Merge each group into a single absence with all practiceIds combined
      const mergedAbsences: ProviderAbsence[] = Array.from(
        absenceGroups.values(),
      ).map((group) => {
        return mergeAbsenceGroup(group);
      });

      return { absences: mergedAbsences, loading };
    } else {
      throw new Error(
        responseData.message ||
          'Failed to fetch batch provider absences',
      );
    }
  } catch (error) {
    loading = false;
    console.error('Failed to fetch batch provider absences:', error);
    throw new Error('Failed to fetch batch provider absences');
  }
};

export const createProviderAbsence = async (
  payload: ProviderAbsenceCreatePayload,
): Promise<{ data: unknown | null; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer ID is not available');
  }

  const url = `${BASE_URL}/create?customerId=${customer}`;
  let loading = true;

  try {
    const responseData = await post(url, { ...payload });
    invalidateProviderAbsenceListCache();
    loading = false;
    return { data: responseData, loading };
  } catch (error) {
    loading = false;
    console.error('Failed to create provider absence:', error);
    throw new Error('Failed to create provider absence');
  }
};

export const updateProviderAbsence = async (
  payload: ProviderAbsenceUpdatePayload,
): Promise<{ data: unknown | null; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer ID is not available');
  }

  const url = `${BASE_URL}/update?customerId=${customer}`;
  let loading = true;

  try {
    const responseData = await put(url, { ...payload });
    invalidateProviderAbsenceListCache();
    loading = false;
    return { data: responseData, loading };
  } catch (error) {
    loading = false;
    console.error('Failed to update provider absence:', error);
    throw new Error('Failed to update provider absence');
  }
};

export const deleteProviderAbsence = async (
  ids: string[],
): Promise<{ data: unknown | null; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer ID is not available');
  }

  // Deduplicate IDs to prevent sending duplicate IDs in the request
  const uniqueIds = Array.from(
    new Set(ids.filter((id): id is string => Boolean(id?.trim()))),
  );

  // Build query string with multiple ids parameters: ids=id1&ids=id2&ids=id3
  const queryParams = buildQueryString({
    customerId: customer,
    ids: uniqueIds,
  });

  const url = `${BASE_URL}/delete?${queryParams}`;
  let loading = true;

  try {
    const responseData = await remove(url);
    invalidateProviderAbsenceListCache();
    loading = false;
    return { data: responseData, loading };
  } catch (error) {
    loading = false;
    console.error('Failed to delete provider absence:', error);
    throw new Error('Failed to delete provider absence');
  }
};
